<template>
  <div>
    <Panel
      :icon="['fas', 'chart-line']"
      loader-text="Loading Campaigns"
      :loading="loading"
      :show-loading-error="showLoadingError"
      :loading-error-text="loadingErrorText"
    >
      <template #header>
        <span data-cy="heading">Campaigns</span>
      </template>
      <template #content>
        <b-table
          data-cy="table-buyer-campaigns"
          :tbody-tr-attr="{ 'data-cy': 'table-row' }"
          :items="visibleCampaigns"
          :fields="fields"
        >
          <template #cell(status)="row">
            <b-badge :variant="getBadgeVariantForCampaignStatus(row.item)">
              {{ getStatusText(row.item) }}
            </b-badge>
          </template>

          <template #cell(category.name)="row">
            {{ row.item.category.name }}
            <b-badge
              v-if="row.item.category.isCpaConversionOnly"
              variant="info"
              class="ml-1"
              title="CPA Conversion Only Category"
            >
              CPA
            </b-badge>
          </template>

          <template #cell(maxBidPerCallInUSD)="row">
            {{ getMaxBidPerCall(row.item) }}
          </template>

          <template #cell(dailyBudgetInUSD)="row">
            {{ formatCurrency(row.item.budget.dailyBudgetInUSD) }}
          </template>

          <template #cell(actions)="row">
            <b-button
              v-if="canPauseCampaign(row.item) && !isCampaignPaused(row.item)"
              v-b-tooltip.hover.left
              class="bg-transparent border-0 p-0"
              size="sm"
              title="Pause Campaign"
              @click="showConfirmPauseBuyerCampaign(row.item)"
            >
              <font-awesome-icon
                :icon="['fa', 'pause']"
              />
            </b-button>
            <b-button
              v-if="canPauseCampaign(row.item) && isCampaignPaused(row.item)"
              v-b-tooltip.hover.left
              class="bg-transparent border-0 p-0"
              size="sm"
              title="Resume Campaign"
              @click="showConfirmResumeBuyerCampaign(row.item)"
            >
              <font-awesome-icon
                :icon="['fa', 'play']"
              />
            </b-button>
            <b-button
              v-if="showSoftphoneButton(row.item)"
              v-b-tooltip.hover.left
              class="ml-3"
              size="sm"
              :variant="isLiveSoftphoneCampaign(row.item) ? 'danger' : 'success'"
              :disabled="isSoftphoneButtonDisabled(row.item)"
              :title="softphoneButtonTitle(row.item)"
              :data-cy="isLiveSoftphoneCampaign(row.item) ? 'button-softphone-go-offline' : 'button-softphone-go-live'"
              @click="toggleSoftphoneForCampaign(row.item)"
            >
              <font-awesome-icon
                :icon="['fa', isLiveSoftphoneCampaign(row.item) ? 'phone-slash' : 'phone']"
                class="mr-1"
              />
              {{ isLiveSoftphoneCampaign(row.item) ? 'Go Offline' : 'Go Live' }}
            </b-button>
            <b-button
              :to="{
                name: editRouteName(row.item),
                params: { id: row.item.id }
              }"
              data-cy="button-edit"
              class="bg-transparent border-0 p-0 ml-3"
              size="sm"
            >
              <font-awesome-icon
                :icon="['fa', 'edit']"
              />
            </b-button>
          </template>
        </b-table>
      </template>
    </Panel>

    <b-modal
      id="confirm-pause-modal"
      ref="confirm-pause-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Pause"
      ok-title="Pause"
      button-size="sm"
      @ok="pauseBuyerCampaign"
    >
      <p
        v-if="selectedBuyerCampaign"
        class="mb-2"
      >
        Are you sure you want to pause <strong>{{ selectedBuyerCampaign.name }}</strong>?
      </p>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('confirm-pause-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          @click="pauseBuyerCampaign"
        >
          Pause
        </BaseButtonLoader>
      </template>
    </b-modal>

    <b-modal
      id="confirm-resume-modal"
      ref="confirm-resume-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Resume"
      ok-title="Resume"
      button-size="sm"
      @ok="resumeBuyerCampaign"
    >
      <p
        v-if="selectedBuyerCampaign"
        class="mb-2"
      >
        Are you sure you want to resume <strong>{{ selectedBuyerCampaign.name }}</strong>?
      </p>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('confirm-resume-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          @click="resumeBuyerCampaign"
        >
          Resume
        </BaseButtonLoader>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import formatCurrency from '@/utils/formatCurrency'
import { showErrorMessage, showSuccessMessage, showWarningMessage } from '@/notification-utils'
import { canSoftphoneCampaignGoLive, getBadgeVariantForCampaignStatus, getCampaignRoutingLabel, getStatusText, isSoftphoneCampaign, PAUSED_STATUS, campaignRoutingTypes } from '@/store/modules/campaign.store'
import { SOFTPHONE_STATUS } from '@/store/modules/softphone.store'

// A short grace period after the agent goes offline before Go Live is offered again: the widget
// pauses the campaign as it shuts down, and going live again inside that window races the pause.
const GO_LIVE_COOLDOWN_MS = 1000

// How long the softphone is given to register before the agent is told it has not connected.
// Generous on purpose: it fetches a WebRTC token, opens a websocket and registers - and when the
// softphone is running in another tab, all of that happens over there.
const SOFTPHONE_CONNECT_TIMEOUT_MS = 30000

// A failed go-live resets the widget to offline first and sets the reason a mutation later. This is
// how long the offline is given to be followed by that reason before the agent is told without one.
const SOFTPHONE_FAILURE_REASON_GRACE_MS = 500

// The agent is taking calls in every one of these - READY is the usual one, the rest cover a call
// that arrives before the mirror has seen the widget go ready.
const CONNECTED_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.READY,
  SOFTPHONE_STATUS.RINGING,
  SOFTPHONE_STATUS.ACTIVE,
  SOFTPHONE_STATUS.WRAP_UP
])

export default {
  name: 'ListBuyerCampaigns',

  title: 'Campaigns',

  data() {
    return {
      loading: true,
      showLoadingError: false,
      loadingErrorText: '',
      selectedBuyerCampaign: null,
      isLoading: false,
      isTogglingSoftphone: false,
      isGoLiveCoolingDown: false,
      goLiveCooldownTimer: null,
      fields: Object.freeze([
        {
          key: 'status',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center'
        },
        {
          key: 'name',
          sortable: true,
        },
        {
          key: 'category.name',
          label: 'Category',
          sortable: true,
        },
        {
          key: 'country.name',
          label: 'Country',
          sortable: true,
        },
        {
          key: 'language.name',
          label: 'Language',
          sortable: true,
        },
        {
          key: 'routing.type',
          label: 'Routing',
          sortable: true,
          formatter: (value, key, item) => {
            return getCampaignRoutingLabel(item, this.startCase)
          }
        },
        {
          key: 'trafficSources.callType',
          label: 'Call Type',
          sortable: true
        },
        {
          key: 'maxBidPerCallInUSD',
          label: 'Bid',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'dailyBudgetInUSD',
          label: 'Daily Budget',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'actions',
          tdAttr: {
            'data-cy': 'table-column-actions'
          }
        }
      ])
    }
  },

  computed: {
    ...mapState('campaign', [
      'buyerCampaigns'
    ]),

    ...mapGetters('globalSettings', ['isSoftphoneUser']),

    ...mapGetters('softphone', [
      'isSoftphoneAvailable',
      'liveCampaignId'
    ]),

    // The list is partitioned by user type rather than filtered: a softphone user sees only
    // softphone campaigns, everyone else sees only the standard ones. Campaigns stay on the
    // account either way - this only controls what the signed-in user is shown.
    visibleCampaigns() {
      return this.buyerCampaigns.filter(
        campaign => isSoftphoneCampaign(campaign) === this.isSoftphoneUser
      )
    },
  },

  watch: {
    // Covers every way the agent stops being live - the Go Offline button, the owning tab closing,
    // or another tab taking over - because all of them land here by clearing the live id.
    liveCampaignId(newCampaignId, oldCampaignId) {
      this.hideOpenTooltips()

      if (oldCampaignId && !newCampaignId) {
        this.startGoLiveCooldown()
      }
    },

    // The click disables the button, and a disabled button never receives the mouseleave that
    // would close its tooltip - so the text sat there until the page was clicked elsewhere.
    isTogglingSoftphone() {
      this.hideOpenTooltips()
    }
  },

  async created () {
    try {
      this.loading = true
      await Promise.all([
        this.fetchBuyerCampaigns(),
        this.getSoftphoneGoesLiveSetting()
      ])
    } catch (error) {
      if (error.response.status === 422) {
        this.showLoadingError = true
        this.loadingErrorText = error.response.data.title
      } else {
        showErrorMessage(error.response?.data?.title)
      }
    } finally {
      this.loading = false
    }
  },

  beforeDestroy() {
    this.clearGoLiveCooldown()
  },

  methods: {
    ...mapActions('campaign', [
      'fetchBuyerCampaigns'
    ]),

    ...mapActions('globalSettings', ['getSoftphoneGoesLiveSetting']),

    ...mapActions('campaign', {
      setResumeBuyerCampaignStatus: 'resumeBuyerCampaign',
      setPauseBuyerCampaignStatus: 'pauseBuyerCampaign',
      goLiveSoftphoneCampaign: 'goLiveSoftphoneCampaign'
    }),

    ...mapActions('softphone', [
      'requestGoOnline',
      'requestGoOffline'
    ]),

    getBadgeVariantForCampaignStatus,

    getStatusText,

    isSoftphoneCampaign,

    formatCurrency,

    isCampaignPaused(campaign) {
      return campaign.status === PAUSED_STATUS || campaign.isPaused
    },

    // A softphone campaign is paused and resumed by going offline and live - the agent has to be
    // connected to take calls, so pausing or resuming it on its own would desync the widget.
    canPauseCampaign(campaign) {
      return !isSoftphoneCampaign(campaign)
    },

    showConfirmPauseBuyerCampaign(campaign) {
      this.selectedBuyerCampaign = campaign

      this.$bvModal.show('confirm-pause-modal')
    },

    async pauseBuyerCampaign() {
      try {
        this.isLoading = true
        await this.setPauseBuyerCampaignStatus({ campaignId: this.selectedBuyerCampaign.id })

        showSuccessMessage('Successfully paused buyer campaign')

        this.selectedBuyerCampaign = null

        this.$bvModal.hide('confirm-pause-modal')
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.isLoading = false
      }
    },

    showConfirmResumeBuyerCampaign(campaign) {
      this.selectedBuyerCampaign = campaign

      this.$bvModal.show('confirm-resume-modal')
    },

    async resumeBuyerCampaign() {
      try {
        this.isLoading = true
        await this.setResumeBuyerCampaignStatus({ campaignId: this.selectedBuyerCampaign.id, status: this.selectedBuyerCampaign.status })

        showSuccessMessage('Successfully resumed buyer campaign')

        this.selectedBuyerCampaign = null

        this.$bvModal.hide('confirm-resume-modal')
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.isLoading = false
      }
    },

    // Closes any tooltip currently open anywhere on the page. Used whenever a button this list
    // owns is about to be disabled, relabelled or removed under the pointer.
    hideOpenTooltips() {
      this.$root.$emit('bv::hide::tooltip')
    },

    isLiveSoftphoneCampaign(campaign) {
      return this.liveCampaignId === campaign.id
    },

    /**
     * Going live is offered only where it can work. Approval is what creates the target the calls
     * arrive through, and a strict pause can only be lifted by an administrator, so on those rows
     * the button was permanently dead - the status badge already says why, and a disabled button
     * next to it only invites clicking.
     *
     * The row the agent is live for keeps its button whatever the campaign looks like now: that
     * button is Go Offline, and it is the only way back off a campaign an administrator has since
     * strict-paused.
     */
    showSoftphoneButton(campaign) {
      if (!this.isSoftphoneAvailable || !isSoftphoneCampaign(campaign)) {
        return false
      }

      if (this.isLiveSoftphoneCampaign(campaign)) {
        return true
      }

      return canSoftphoneCampaignGoLive(campaign) && !this.isStrictPausedCampaign(campaign)
    },

    // An administrator paused it and only an administrator can resume it. Going live resumes the
    // campaign first, and the resume endpoint rejects a strict pause, so offering the button would
    // only open a window and then fail.
    isStrictPausedCampaign(campaign) {
      return campaign?.isStrictPause === true
    },

    // Once the agent is live for one campaign every other Go Live is dead: they have one pair of
    // ears, and a second campaign routing to the same widget would be a call nobody answers.
    isSoftphoneButtonDisabled(campaign) {
      if (this.isTogglingSoftphone || this.isGoLiveCoolingDown) {
        return true
      }

      if (this.isLiveSoftphoneCampaign(campaign)) {
        return false
      }

      return !!this.liveCampaignId
        || this.isStrictPausedCampaign(campaign)
        || !canSoftphoneCampaignGoLive(campaign)
    },

    softphoneButtonTitle(campaign) {
      if (this.isLiveSoftphoneCampaign(campaign)) {
        return 'Stop taking calls and pause this campaign'
      }

      if (this.isStrictPausedCampaign(campaign)) {
        return campaign.strictPauseReason
          ? `Paused by an administrator: ${campaign.strictPauseReason}`
          : 'Paused by an administrator and can only be resumed by one'
      }

      // Approval is what creates the target the calls arrive through, so an unapproved campaign
      // cannot ring however live the agent is. Said here rather than left to the go-live refusal:
      // the status badge alone does not explain why the button does nothing.
      if (!canSoftphoneCampaignGoLive(campaign)) {
        return campaign.status === 'InReview'
          ? 'Waiting for approval - you can go live once this campaign is approved'
          : 'This campaign cannot take calls'
      }

      if (this.isGoLiveCoolingDown) {
        return 'Finishing up your last session'
      }

      // The agent's own click is what disabled this button, and the go-live it started may still
      // be in flight - telling them they are live for ANOTHER campaign reads as a refusal of the
      // one they just clicked.
      if (this.isTogglingSoftphone) {
        return 'Already live'
      }

      if (this.isSoftphoneButtonDisabled(campaign)) {
        return 'You are already live for another campaign'
      }

      return 'Start taking calls for this campaign in this tab'
    },

    async toggleSoftphoneForCampaign(campaign) {
      if (this.isLiveSoftphoneCampaign(campaign)) {
        await this.goOfflineForCampaign()

        return
      }

      await this.goLiveForCampaign(campaign)
    },

    /**
     * Order matters and is not obvious.
     *
     * The server is told to go live FIRST - it pauses the agent's other softphone campaigns and
     * resumes this one - and only once that succeeds is the softphone told to connect, so a
     * go-live failure never leaves an agent taking calls for a campaign that is still paused.
     */
    async goLiveForCampaign(campaign) {
      // Belt and braces for the disabled button: never go live for a campaign the server is
      // guaranteed to refuse.
      if (this.isStrictPausedCampaign(campaign) || !canSoftphoneCampaignGoLive(campaign)) {
        showErrorMessage(this.softphoneButtonTitle(campaign))

        return
      }

      try {
        this.isTogglingSoftphone = true

        await this.goLiveSoftphoneCampaign({ campaignId: campaign.id, status: campaign.status })

        // The whole row: the action picks off what the softphone needs, including the budget's
        // minimum duration, which a tab that holds the softphone without holding this list has no
        // other way to learn.
        //
        // Not awaited, and its failure deliberately swallowed: going live reports itself through
        // the softphone's status, which is what `waitForSoftphoneToConnect` below reads - in this
        // tab or in whichever one owns the transport.
        Promise.resolve(this.requestGoOnline(campaign)).catch(() => {})

        // The server resuming the campaign is only half of going live - the agent is not taking
        // calls until the softphone has registered. Announcing success on the server call alone
        // told agents they were live while the softphone was still failing to connect.
        const connection = await this.waitForSoftphoneToConnect()

        if (connection.status === 'failed') {
          showErrorMessage(connection.error || 'The softphone could not connect, so you are not taking calls')

          return
        }

        if (connection.status === 'timedOut') {
          showWarningMessage('The campaign was resumed, but the softphone has not connected yet - check the softphone in the header')

          return
        }

        // No success notification: the softphone panel opens itself on going live, and a panel
        // reading Ready says the same thing in the place the agent is about to work from. See the
        // `softphoneAttention` watcher in SoftphoneHeaderWidget.
      } catch (error) {
        showErrorMessage(error.response?.data?.title || 'Could not go live for this campaign')
      } finally {
        this.isTogglingSoftphone = false
      }
    },

    /**
     * Resolves once the softphone has said what became of the go-live: `connected` when it is
     * registered and taking calls, `failed` when it gave up (the token call, the transport, or the
     * owning tab going away on the way), `timedOut` when it has said nothing either way.
     *
     * Reads the softphone store's status and nothing else, which works whether this tab owns the
     * transport or is only being told about it.
     */
    waitForSoftphoneToConnect() {
      return new Promise(resolve => {
        let unwatch = null
        let timeoutId = null

        // A failed go-live resets the widget to OFFLINE and only then sets ERROR with the reason,
        // so a bare OFFLINE waits a moment for that reason rather than reporting without it.
        let failureGraceTimerId = null

        let hasStartedConnecting = false

        const finish = result => {
          if (unwatch) {
            unwatch()
            unwatch = null
          }

          clearTimeout(timeoutId)
          clearTimeout(failureGraceTimerId)

          timeoutId = null
          failureGraceTimerId = null

          resolve(result)
        }

        const fail = () => finish({ status: 'failed', error: this.$store.state.softphone.lastError })

        const evaluate = status => {
          if (status === SOFTPHONE_STATUS.CONNECTING) {
            hasStartedConnecting = true

            return false
          }

          if (CONNECTED_STATUSES.includes(status)) {
            finish({ status: 'connected' })

            return true
          }

          // ERROR is the softphone's own report that it could not go live - it broadcasts the
          // reason with it. OTHER_TAB is another tab taking the softphone over, which leaves this
          // go-live just as dead.
          if (status === SOFTPHONE_STATUS.ERROR || status === SOFTPHONE_STATUS.OTHER_TAB) {
            fail()

            return true
          }

          // Back to offline after it began connecting: the softphone tore down, whether it went on
          // to say why or its tab simply went away. Offline BEFORE that is the softphone dropping a
          // previous campaign on its way to this one, which is part of a normal go-live.
          if (status === SOFTPHONE_STATUS.OFFLINE && hasStartedConnecting && !failureGraceTimerId) {
            failureGraceTimerId = setTimeout(fail, SOFTPHONE_FAILURE_REASON_GRACE_MS)
          }

          return false
        }

        // The widget can register before this tab gets to subscribe.
        if (evaluate(this.$store.state.softphone.status)) {
          return
        }

        unwatch = this.$store.watch(state => state.softphone.status, evaluate)

        timeoutId = setTimeout(() => finish({ status: 'timedOut' }), SOFTPHONE_CONNECT_TIMEOUT_MS)
      })
    },

    /**
     * The owning tab goes offline end to end - it disconnects and pauses the campaign, in that
     * order, then broadcasts the result so every other tab's rows come back to life. When that tab
     * is this one the action simply does it; when it is another, this asks.
     */
    async goOfflineForCampaign() {
      try {
        this.isTogglingSoftphone = true

        // No success notification either way: the softphone panel and the row's own button both
        // say what happened, and they say it where the agent is looking.
        await this.requestGoOffline()
      } catch (error) {
        showErrorMessage('Could not take you offline')
      } finally {
        this.isTogglingSoftphone = false
      }
    },

    startGoLiveCooldown() {
      this.clearGoLiveCooldown()

      this.isGoLiveCoolingDown = true

      this.goLiveCooldownTimer = setTimeout(() => {
        this.goLiveCooldownTimer = null
        this.isGoLiveCoolingDown = false
      }, GO_LIVE_COOLDOWN_MS)
    },

    clearGoLiveCooldown() {
      if (this.goLiveCooldownTimer) {
        clearTimeout(this.goLiveCooldownTimer)
        this.goLiveCooldownTimer = null
      }

      this.isGoLiveCoolingDown = false
    },

    // Softphone campaigns open in their own reduced page - the standard edit page's Number/SIP
    // destination form cannot represent a softphone destination.
    editRouteName(campaign) {
      return isSoftphoneCampaign(campaign) ? 'BuyerEditSoftphoneCampaign' : 'BuyerEditCampaign'
    },

    getMaxBidPerCall(campaign) {
      return campaign.routing.type === campaignRoutingTypes.Static ? this.formatCurrency(campaign.budget.maxBidPerCallInUSD) : 'Dynamic'
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .panel__header h6 {
  flex-grow: 1;
}
</style>